import { create } from "zustand";
import { toast } from "react-toastify";
import {
  addOrUpdateRfidMaster,
  getAllInstalledAreas,
  getRfidTypes,
  getAssetCategoryByRfidType,
  getAllRfidCategories,
  getAllRfidMaster,
  getRfidStatusHistory,
  mapInstalledAreaOptions,
} from "../../../Apis/rfidApi";
import {
  mapRfidBackendErrors,
  isRfidSaveSuccess,
  toRfidEditableValue,
} from "../rfidFormValidation";
import { mapRfidHistoryData, getRfidHistoryKey } from "../utils/rfidHistoryUtils";

const getErrorMessage = (data, fallback) => {
  if (typeof data === "string" && data) return data;
  if (data?.message) return data.message;
  return fallback;
};

const extractRfidList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.rfidMasters)) return response.rfidMasters;
  return [];
};

const mapRfidData = (list = []) =>
  list.map((item) => ({
    ...item,
    rfidMasterId: item.rfidMasterId ?? item.assetRfidId ?? item.rfidId,
    assetRfidId: item.rfidMasterId ?? item.assetRfidId ?? item.rfidId,
    rfidCode: item.rfidCode || item.assetRfidCode || "-",
    rfidCategoryName:
      item.rfidCategory ?? item.rfidCategoryName ?? item.assetCategoryName ?? "N/A",
    rfidDeviceName:
      item.rfDeviceName ?? item.rfidDeviceName ?? item.deviceName ?? "N/A",
    // Keep real values for edit/add validation (table shows N/A/- when empty).
    imeiSlMfgNo: toRfidEditableValue(item.imeiNo ?? item.imeiSlMfgNo ?? ""),
    zoneName: item.zoneName || "N/A",
    zoneId: item.zoneId ?? item.ZoneId ?? "",
    circleId: item.circleId ?? item.CircleId ?? "",
    assetCategoryId: item.assetCategoryId ?? item.AssetCategoryId ?? "",
    wardNo: item.wardName ?? item.wardNo ?? item.circleName ?? "N/A",
    locationLandmark: item.location ?? item.locationLandmark ?? "N/A",
    assetAreaId: item.assetAreaId ?? item.installedAreaId ?? "",
    installedArea:
      item.installedArea ?? item.assetAreaType ?? item.installedAreaName ?? "N/A",
    otherNo: toRfidEditableValue(item.otherNo ?? ""),
    collectionTypeId: item.collectionTypeId ?? item.CollectionTypeId ?? "",
    vehicleTypeId: item.vehicleTypeId ?? item.VehicleTypeId ?? "",
    vehicleCapacityId: item.vehicleCapacityId ?? item.VehicleCapacityId ?? "",
    ownerTypeId: item.ownerTypeId ?? item.OwnerTypeId ?? "",
    rfidTypeId: item.rfidTypeId ?? item.assetTypeId ?? item.RFIDTypeId ?? "",
    rfidTypeName:
      item.rfidTypeName ??
      item.assetTypeName ??
      item.rfidType ??
      item.RFIDType ??
      "",
    warrantyExpireDate:
      item.warrantyEXPDate ??
      item.warrantyExpireDate ??
      item.WarrantyExpireDate ??
      "",
    purchaseDate:
      item.dateOfPurchase ?? item.PurchaseDate ?? item.purchaseDate ?? "",
    insuranceNo: item.insuranceNo ?? item.InsuranceNo ?? "",
    insuranceValidity: item.insuranceValidity ?? item.InsuranceValidity ?? "",
    insuranceProvider: item.insuranceProvider ?? item.InsuranceProvider ?? "",
    dateOfInstallation:
      item.installDate ?? item.InstallDate ?? item.dateOfInstallation ?? "",
    installationRemarks:
      item.installationRemarks ??
      item.InstallationRemarks ??
      item.remarks ??
      item.Remarks ??
      "",
    installedBy: item.installedBy ?? item.createdByName ?? "N/A",
    lastModifiedOn: item.lastModifiedOn ?? item.modifiedOn ?? item.updatedDate ?? "N/A",
    latitude: item.lat ?? item.latitude ?? "",
    longitude: item.lng ?? item.longitude ?? "",
    imageUrl:
      item.imgUrl ??
      item.ImgUrl ??
      item.imageUrl ??
      item.ImageUrl ??
      "",
    isActive: item.isActive ?? item.status ?? true,
    history: Array.isArray(item.history) ? item.history : [],
  }));

const sortActiveFirst = (list = []) =>
  [...list].sort(
    (a, b) => Number(Boolean(b.isActive)) - Number(Boolean(a.isActive))
  );

const buildRfidMasterParams = (filters = {}, pagination = {}) => ({
  zoneId: filters.zoneId || undefined,
  circleId: filters.circleId || undefined,
  assetCategoryId: filters.assetCategoryId || undefined,
  shift: filters.shift || undefined,
  fromDate: filters.fromDate || undefined,
  toDate: filters.toDate || undefined,
  pageNumber: pagination.pageNumber ?? 1,
  pageSize: pagination.pageSize ?? 100,
});

// The API pages records without regard to isActive, so a server-paginated page can
// land a mix of active/inactive rows (inactive ones showing up as early as page 1).
// To guarantee inactive rows always trail behind every active row across the whole
// filtered result set (i.e. only ever appear on the last page(s)), fetch the full
// matching set once, sort active-first, then paginate for display on the client.
const BULK_FETCH_PAGE_SIZE = 5000;

const paginateList = (list = [], pageNumber = 1, pageSize = 100) => {
  const start = (pageNumber - 1) * pageSize;
  return list.slice(start, start + pageSize);
};

export const DEFAULT_RFID_ZONE_ID = "1";
export const DEFAULT_RFID_ZONE_NAME = "Delhi Central Zone";

export const useRfidStore = create((set, get) => ({
  rfidDevices: [],
  filteredRfidDevices: [],
  rfidCategories: [],
  assetTypes: [],
  installedAreas: [],
  rfidHistory: {},
  isLoading: false,
  isSaving: false,
  isHistoryLoading: false,
  error: "",
  pagination: {
    pageNumber: 1,
    pageSize: 100,
    totalRecords: 0,
    totalPages: 1,
  },
  filters: {
    zoneId: DEFAULT_RFID_ZONE_ID,
    circleId: "",
    assetCategoryId: "",
    searchTerm: "",
    shift: "",
    fromDate: "",
    toDate: "",
  },

  updateFilter: (name, value) =>
    set((state) => ({
      filters: { ...state.filters, [name]: value },
    })),

  resetFilters: () => {
    set({
      filters: {
        zoneId: DEFAULT_RFID_ZONE_ID,
        circleId: "",
        assetCategoryId: "",
        searchTerm: "",
        shift: "",
        fromDate: "",
        toDate: "",
      },
      pagination: { ...get().pagination, pageNumber: 1 },
    });
    return get().fetchRfidDevices({
      pageNumber: 1,
      zoneId: DEFAULT_RFID_ZONE_ID,
    });
  },

  setRfidPage: (pageNumber) => {
    const { rfidDevices, pagination } = get();
    const totalPages = Math.max(
      1,
      Math.ceil(rfidDevices.length / pagination.pageSize)
    );
    const safePageNumber = Math.min(Math.max(1, pageNumber), totalPages);

    set({
      filteredRfidDevices: paginateList(
        rfidDevices,
        safePageNumber,
        pagination.pageSize
      ),
      pagination: { ...pagination, pageNumber: safePageNumber, totalPages },
    });
  },

  setRfidPageSize: (pageSize) => {
    const { rfidDevices } = get();
    const totalRecords = rfidDevices.length;
    const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

    set({
      filteredRfidDevices: paginateList(rfidDevices, 1, pageSize),
      pagination: { pageNumber: 1, pageSize, totalRecords, totalPages },
    });
  },

  applyFilters: () => get().fetchRfidDevices({ pageNumber: 1 }),

  fetchAssetTypes: async () => {
    try {
      const typeList = await getRfidTypes();
      set({ assetTypes: typeList });
      return typeList;
    } catch {
      set({ assetTypes: [] });
      return [];
    }
  },

  fetchRfidCategories: async () => {
    try {
      const categoryList = await getAllRfidCategories();
      set({
        rfidCategories: categoryList.filter((category) => category.isActive),
      });
      return categoryList;
    } catch {
      set({ rfidCategories: [] });
      return [];
    }
  },

  fetchInstalledAreas: async () => {
    try {
      const areaList = await getAllInstalledAreas();
      const mappedAreas = mapInstalledAreaOptions(areaList);
      set({ installedAreas: mappedAreas });
      return mappedAreas;
    } catch {
      set({ installedAreas: [] });
      toast.error("Failed to load installed areas.");
      return [];
    }
  },

  fetchRfidDevices: async (queryParams = {}) => {
    set({ isLoading: true, error: "" });
    try {
      const { filters, pagination } = get();
      const mergedFilters = { ...filters, ...queryParams };
      const pageNumber = queryParams.pageNumber ?? pagination.pageNumber;
      const pageSize = queryParams.pageSize ?? pagination.pageSize;
      const params = buildRfidMasterParams(mergedFilters, {
        pageNumber: 1,
        pageSize: BULK_FETCH_PAGE_SIZE,
      });

      const response = await getAllRfidMaster(params);
      const deviceList = extractRfidList(response);
      const mappedData = sortActiveFirst(mapRfidData(deviceList));

      if (response?.totalRecords > mappedData.length) {
        console.warn(
          `RFID list truncated: server reports ${response.totalRecords} records ` +
            `but only ${mappedData.length} were fetched (bulk fetch cap is ${BULK_FETCH_PAGE_SIZE}).`
        );
      }

      const totalRecords = mappedData.length;
      const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
      const safePageNumber = Math.min(Math.max(1, pageNumber), totalPages);

      set({
        rfidDevices: mappedData,
        filteredRfidDevices: paginateList(mappedData, safePageNumber, pageSize),
        pagination: {
          pageNumber: safePageNumber,
          pageSize,
          totalRecords,
          totalPages,
        },
      });

      return { success: true, data: mappedData };
    } catch (error) {
      const message = getErrorMessage(
        error?.response?.data,
        "Failed to load RFID data."
      );
      set({
        rfidDevices: [],
        filteredRfidDevices: [],
        error: message,
        pagination: { pageNumber: 1, pageSize: 100, totalRecords: 0, totalPages: 1 },
      });
      toast.error(message);
      return { success: false, message };
    } finally {
      set({ isLoading: false });
    }
  },

  fetchRfidHistory: async (rfidMasterId) => {
    const historyKey = getRfidHistoryKey(rfidMasterId);
    if (!historyKey) return [];
    set({ isHistoryLoading: true });
    try {
      const historyList = await getRfidStatusHistory(rfidMasterId);
      const mappedHistory = mapRfidHistoryData(historyList);
      set((state) => ({
        rfidHistory: { ...state.rfidHistory, [historyKey]: mappedHistory },
      }));
      return mappedHistory;
    } catch (error) {
      const message = getErrorMessage(
        error?.response?.data,
        "Failed to load RFID status history."
      );
      toast.error(message);
      set((state) => ({
        rfidHistory: { ...state.rfidHistory, [historyKey]: [] },
      }));
      return [];
    } finally {
      set({ isHistoryLoading: false });
    }
  },

  saveRfid: async (payload, isEdit = false) => {
    set({ isSaving: true, error: "" });
    try {
      const requestPayload = {
        rfidMasterId: isEdit
          ? Number(payload.rfidMasterId ?? payload.assetRfidId)
          : 0,
        assetCategoryId: Number(payload.assetCategoryId),
        rfDeviceName: payload.rfidDeviceName?.trim(),
        imeiNo: payload.imeiSlMfgNo?.trim(),
        location: payload.locationLandmark?.trim(),
        zoneId: Number(payload.zoneId),
        circleId: Number(payload.circleId),
        assetAreaId: Number(payload.assetAreaId),
        otherNo: payload.otherNo?.trim() || "",
        collectionTypeId: payload.collectionTypeId
          ? Number(payload.collectionTypeId)
          : "",
        vehicleTypeId: payload.vehicleTypeId ? Number(payload.vehicleTypeId) : "",
        vehicleCapacityId: payload.vehicleCapacityId
          ? Number(payload.vehicleCapacityId)
          : "",
        ownerTypeId: payload.ownerTypeId ? Number(payload.ownerTypeId) : "",
        installDate: payload.dateOfInstallation || "",
        dateOfPurchase: payload.purchaseDate || "",
        rfidTypeId: payload.rfidTypeId ? Number(payload.rfidTypeId) : "",
        warrantyExpireDate: payload.warrantyExpireDate || "",
        insuranceNo: payload.insuranceNo?.trim() || "",
        insuranceValidity: payload.insuranceValidity || "",
        insuranceProvider: payload.insuranceProvider?.trim() || "",
        lat: payload.latitude,
        lng: payload.longitude,
        installationRemarks: payload.installationRemarks?.trim() || "",
        isActive: Boolean(payload.isActive),
        imageFile: payload.imageFile || null,
        imgUrl: isEdit
          ? payload.imgUrl ?? payload.imageUrl ?? ""
          : "",
      };

      const response = await addOrUpdateRfidMaster(requestPayload);

      if (isRfidSaveSuccess(response)) {
        const message = getErrorMessage(
          response,
          isEdit ? "RFID updated successfully." : "RFID created successfully."
        );
        toast.success(message);
        await get().fetchRfidDevices();
        return { success: true };
      }

      const isApiFailure =
        response?.success === false ||
        response?.isSuccess === false ||
        (response?.result != null && Number(response.result) !== 1);

      if (isApiFailure) {
        const message = getErrorMessage(
          response,
          "Unable to save RFID details."
        );
        const fieldErrors = mapRfidBackendErrors(response, message);

        if (!Object.keys(fieldErrors).length) {
          toast.error(message);
        }

        return { success: false, message, fieldErrors };
      }

      const message = getErrorMessage(
        response,
        isEdit ? "RFID updated successfully." : "RFID created successfully."
      );
      toast.success(message);
      await get().fetchRfidDevices();
      return { success: true };
    } catch (error) {
      const responseData = error?.response?.data;

      if (isRfidSaveSuccess(responseData)) {
        const message = getErrorMessage(
          responseData,
          isEdit ? "RFID updated successfully." : "RFID created successfully."
        );
        toast.success(message);
        await get().fetchRfidDevices();
        return { success: true };
      }

      const message = getErrorMessage(
        responseData,
        "Unable to save RFID details."
      );
      const fieldErrors = mapRfidBackendErrors(responseData, message);
      set({ error: message });

      if (!Object.keys(fieldErrors).length) {
        toast.error(message);
      }

      return { success: false, message, fieldErrors };
    } finally {
      set({ isSaving: false });
    }
  },
}));
